/**
 * Renders the "claim your unclaimed items" modal and provides `promptClaim` to open it. Mounted
 * once above the router; a page calls `useRevealClaim().promptClaim(hint)` from a `reveal_pending`
 * error handler. The modal claims the specific blocker the error named — an unclaimed box reveal
 * or a completed battle's winnings — or sells it straight back for its value, and offers an
 * auto-claim toggle so future reveals settle on their own.
 */
import {
  capitalizeTerm,
  formatAmountWithUnit,
  type StorefrontTerminology,
} from "@loot-os/api-contract";
import { isLootApiErrorLike } from "@loot-os/react";
import { Button } from "@loot-os/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@loot-os/ui/components/dialog";
import { Switch } from "@loot-os/ui/components/switch";
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";

import { track } from "../lib/analytics.js";
import { useCurrency } from "../lib/currency.js";
import {
  battleQueryOptions,
  lootMeQueryOptions,
  unclaimedRevealQueryOptions,
} from "../lib/queries.js";
import {
  type PendingRevealHint,
  RevealClaimContext,
  useClaimBattleWinnings,
  useClaimBoxReveal,
  useSellBackBattleWinnings,
  useSellBackBoxReveal,
  useUpdateAutoClaim,
} from "../lib/revealClaim.js";
import { useShopper } from "../lib/shopper.js";
import { useTerminology } from "../lib/terminology.js";

export function RevealClaimProvider({ children }: { children?: ReactNode }) {
  const [hint, setHint] = useState<PendingRevealHint | null>(null);

  const promptClaim = (next?: PendingRevealHint | null) => {
    setHint(next ?? null);
    if (next) track("claim_prompt_shown", { kind: next.kind });
  };

  return (
    <RevealClaimContext.Provider value={{ promptClaim }}>
      {children}
      <ClaimDialog hint={hint} onClose={() => setHint(null)} />
    </RevealClaimContext.Provider>
  );
}

function ClaimDialog({ hint, onClose }: { hint: PendingRevealHint | null; onClose: () => void }) {
  const open = hint !== null;
  const { sessionToken } = useShopper();
  const currency = useCurrency();
  const terms = useTerminology();
  const { box, item, inventory, battle: battleTerm } = terms;
  const isBattle = hint?.kind === "battle";

  const me = useQuery({ ...lootMeQueryOptions(), enabled: open && !!sessionToken });
  const reveal = useQuery({
    ...unclaimedRevealQueryOptions(),
    enabled: open && !isBattle && !!sessionToken,
  });
  const battle = useQuery({
    ...battleQueryOptions(hint?.kind === "battle" ? hint.battleId : "", { poll: false }),
    enabled: open && isBattle && !!sessionToken,
  });

  const claimBox = useClaimBoxReveal();
  const claimBattle = useClaimBattleWinnings();
  const sellBackBox = useSellBackBoxReveal();
  const sellBackBattle = useSellBackBattleWinnings();
  const setAutoClaim = useUpdateAutoClaim();

  // What we are about to claim, resolved from whichever source the hint points at.
  const boxReveal = reveal.data?.reveal ?? null;
  const battleView = battle.data?.battle ?? null;
  const battleMe = battleView?.me ?? null;
  const iWon = battleMe != null && battleMe.seatIndex === battleView?.winnerSeatIndex;

  const costCents = isBattle ? (battleMe?.costCents ?? null) : (boxReveal?.costCents ?? null);
  const itemCount = isBattle ? null : (boxReveal?.entries.length ?? null);
  const loading = isBattle ? battle.isLoading : reveal.isLoading;
  const nothingToClaim = !loading && costCents === null;
  const loserPrize = isBattle && !iWon ? (battleView?.loserPrizeItem ?? null) : null;
  // What selling back instead credits: the pot for a winner, the prize for a loser, Σ items for a box.
  const sellBackCents = isBattle
    ? battleView === null || battleMe === null
      ? null
      : iWon
        ? battleView.seats.reduce((sum, seat) => sum + (seat.totalValueCents ?? 0), 0)
        : (loserPrize?.value ?? 0)
    : (boxReveal?.sellBackCents ?? null);
  const canSellBack = sellBackCents !== null && sellBackCents > 0;

  const claiming = claimBox.isPending || claimBattle.isPending;
  const sellingBack = sellBackBox.isPending || sellBackBattle.isPending;
  const busy = claiming || sellingBack;
  const actionError =
    claimBox.error ?? claimBattle.error ?? sellBackBox.error ?? sellBackBattle.error;
  const claimError = actionError ? errorText(actionError, terms) : null;

  const onClaim = () => {
    if (isBattle && hint) {
      claimBattle.mutate(hint.battleId, { onSuccess: onClose });
      return;
    }
    const openGroupId =
      boxReveal?.openGroupId ?? (hint?.kind === "open_group" ? hint.openGroupId : "");
    if (openGroupId) claimBox.mutate(openGroupId, { onSuccess: onClose });
  };
  const onSellBack = () => {
    if (isBattle && hint) {
      sellBackBattle.mutate(hint.battleId, { onSuccess: onClose });
      return;
    }
    const openGroupId =
      boxReveal?.openGroupId ?? (hint?.kind === "open_group" ? hint.openGroupId : "");
    if (openGroupId) sellBackBox.mutate(openGroupId, { onSuccess: onClose });
  };

  const heading = isBattle
    ? iWon
      ? "Claim your winnings"
      : loserPrize
        ? "Claim your prize"
        : `${capitalizeTerm(battleTerm.singular)} over`
    : `Claim your unclaimed ${item.plural}`;
  // A battle entry was paid when the seat was taken; a box reveal is paid by its claim.
  const description = isBattle
    ? `Your entry was paid when you took your seat; nothing more is charged.${
        iWon
          ? ` Claim the ${item.plural} to add them to your ${inventory.singular}`
          : loserPrize
            ? ` Claim the ${loserPrize.name} to add it to your ${inventory.singular}`
            : " There is nothing to claim"
      }${canSellBack ? ", or sell straight back for the value" : ""}. You can't open or start anything else until you decide.`
    : `Same reveal-then-claim as opening any ${box.singular}: the price is charged now and the ${item.plural} become yours${
        canSellBack ? ", or sold straight back for their value" : ""
      }. You can't open or start anything else until it's settled.`;
  const claimLabel = isBattle
    ? iWon
      ? `Claim ${item.plural}`
      : loserPrize
        ? "Claim prize"
        : "Done"
    : costCents !== null
      ? `Claim · ${formatAmountWithUnit(costCents, currency)}`
      : "Claim";

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{heading}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">
          {loading ? (
            "Loading…"
          ) : nothingToClaim ? (
            "Nothing left to claim."
          ) : (
            <span>
              {isBattle
                ? iWon
                  ? `${capitalizeTerm(battleTerm.singular)} winnings`
                  : loserPrize
                    ? `${capitalizeTerm(battleTerm.singular)} entry · ${loserPrize.name}`
                    : `${capitalizeTerm(battleTerm.singular)} entry`
                : `${itemCount ?? 0} ${itemCount === 1 ? item.singular : item.plural}`}
              {costCents !== null ? (
                <>
                  {" · "}
                  <strong>
                    {isBattle
                      ? `entry ${formatAmountWithUnit(costCents, currency)} paid`
                      : formatAmountWithUnit(costCents, currency)}
                  </strong>
                </>
              ) : null}
            </span>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 text-sm">
          <div>
            <p className="font-medium">Auto-claim</p>
            <p className="text-xs text-muted-foreground">
              Claim future reveals and {battleTerm.singular} wins automatically.
            </p>
          </div>
          <Switch
            aria-label={`Auto-claim future reveals and ${battleTerm.singular} wins`}
            checked={me.data?.autoClaim ?? false}
            disabled={setAutoClaim.isPending || !me.data}
            onCheckedChange={(checked) => setAutoClaim.mutate(checked)}
          />
        </div>

        {claimError ? <p className="text-sm text-destructive">{claimError}</p> : null}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Not now
          </Button>
          {canSellBack ? (
            <Button
              variant="outline"
              onClick={onSellBack}
              disabled={busy || loading || nothingToClaim}
            >
              {sellingBack
                ? "Selling back…"
                : `Sell back · ${formatAmountWithUnit(sellBackCents, currency)}`}
            </Button>
          ) : null}
          <Button onClick={onClaim} disabled={busy || loading || nothingToClaim}>
            {claiming ? "Claiming…" : claimLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function errorText(err: unknown, terms: StorefrontTerminology): string {
  if (isLootApiErrorLike(err) && err.code === "insufficient_balance") {
    return "Not enough balance to claim — top up and try again.";
  }
  if (isLootApiErrorLike(err) && err.code === "reveal_already_claimed") {
    return `Already claimed — the ${terms.item.plural} are in your ${terms.inventory.singular}.`;
  }
  if (isLootApiErrorLike(err) && err.code === "reveal_already_sold") {
    return "Already sold back.";
  }
  return err instanceof Error ? err.message : "claim failed";
}
